/**
 * productOf — derive the wire-level Product from a UI RasterMode.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md §7.2
 *   - Effect 1 (dates fetch) keys its cache by Product, not RasterMode.
 *   - The 'uniformity' UI mode is *gated* by NDVI presence on the backend
 *     (no separate /satellite/dates?product=UNIFORMITY route), so it shares
 *     the NDVI key with 'ndvi'. Switching ndvi ↔ uniformity must NOT trigger
 *     a refetch of dates.
 *
 * Founder lock-in #1 (spec §13): selectedDate is preserved across product
 *   switches. This helper is the single source of truth for the
 *   RasterMode → Product mapping; consistent here keeps cache keys stable
 *   across the satellite axis.
 */
import type { Product, RasterMode } from '../state/mapState';

/**
 * Returns the backend Product corresponding to a UI raster mode, or
 * `undefined` when no satellite overlay is active ('none'). The exhaustive
 * switch + RasterMode union ensures TypeScript flags any new mode added in
 * future without an explicit Product mapping.
 */
export function productOf(mode: RasterMode): Product | undefined {
  switch (mode) {
    case 'ndvi':
    case 'uniformity':
      // Both gated by NDVI presence on the backend — share cache key.
      return 'NDVI';
    case 'avg_ndvi':
      return 'NDVI_AVG';
    case 'ndvi_contrast':
      return 'NDVI_CONTRAST';
    case 'gnr':
      return 'GNR';
    case 'none':
      return undefined;
  }
}
